class TrSelect extends HTMLElement {
	selectors = {
		button: '[data-tr-js-select-button]',
		native: '[data-tr-js-select-native]',
		option: '[data-tr-js-select-option]',
		list: '[data-tr-js-select-list]',
		value: '[data-tr-js-select-value]'
	};

	attributes = {};

	stateClasses = {
		isActive: 'is-active',
		isOpen: 'is-open'
	};

	connectedCallback() {
		this.buttonElement = this.querySelector(this.selectors.button);
		this.nativeElement = this.querySelector(this.selectors.native);
		this.optionElements = Array.from(this.querySelectorAll(this.selectors.option));
		this.listElement = this.querySelector(this.selectors.list);
		this.valueElement = this.querySelector(this.selectors.value);

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
	}

	disconnectedCallback() {
		document.removeEventListener('shopify:section:load', this.onSectionLoad);

		this.destroy();
	}

	init() {
		this.updateValue();
	}

	bindEvents() {
		if (this.buttonElement) {
			this.buttonElement.addEventListener('click', this.onButtonClick);
			this.buttonElement.addEventListener('keydown', this.onButtonKeydown);
		}

		this.optionElements.forEach((optionElement) => {
			optionElement.addEventListener('click', this.onOptionClick);
			optionElement.addEventListener('keydown', this.onOptionKeydown);
		});

		document.addEventListener('click', this.onDocumentClick);
		document.addEventListener('keydown', this.onDocumentKeydown);
	}

	onButtonClick = () => {
		this.toggle();
	};

	onButtonKeydown = (event) => {
		if (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ') return;

		event.preventDefault();
		this.open();
		this.focusSelectedOption();
	};

	onOptionClick = (event) => {
		this.selectOption(event.currentTarget);
	};

	onOptionKeydown = (event) => {
		const currentIndex = this.optionElements.indexOf(event.currentTarget);
		let nextIndex = currentIndex;

		if (event.key === 'ArrowDown') {
			nextIndex = (currentIndex + 1) % this.optionElements.length;
		} else if (event.key === 'ArrowUp') {
			nextIndex = (currentIndex - 1 + this.optionElements.length) % this.optionElements.length;
		} else if (event.key === 'Home') {
			nextIndex = 0;
		} else if (event.key === 'End') {
			nextIndex = this.optionElements.length - 1;
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.selectOption(event.currentTarget);
			return;
		} else if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			this.buttonElement.focus();
			return;
		} else {
			return;
		}

		event.preventDefault();
		this.optionElements[nextIndex].focus();
	};

	onDocumentClick = (event) => {
		if (this.contains(event.target)) return;

		this.close();
	};

	onDocumentKeydown = (event) => {
		if (event.key !== 'Escape') return;

		this.close();
	};

	onSectionLoad = (event) => {
		if (
			!this.closest(`#${event.detail.sectionId}`) &&
			!this.closest(`#shopify-section-${event.detail.sectionId}`)
		)
			return;

		this.init();
	};

	toggle() {
		if (this.classList.contains(this.stateClasses.isOpen)) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		if (!this.buttonElement || !this.listElement) return;

		this.classList.add(this.stateClasses.isOpen);
		this.buttonElement.setAttribute('aria-expanded', 'true');
		this.listElement.hidden = false;
	}

	close() {
		if (!this.buttonElement || !this.listElement) return;

		this.classList.remove(this.stateClasses.isOpen);
		this.buttonElement.setAttribute('aria-expanded', 'false');
		this.listElement.hidden = true;
	}

	selectOption(optionElement) {
		if (!this.nativeElement) return;

		const optionValue = optionElement.getAttribute('data-tr-js-select-option-value');

		this.nativeElement.value = optionValue;
		this.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
		this.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));

		this.updateValue();
		this.close();
		this.buttonElement.focus();
	}

	updateValue() {
		if (!this.nativeElement || !this.valueElement) return;

		const selectedOptionElement = this.nativeElement.options[this.nativeElement.selectedIndex];

		if (!selectedOptionElement) return;

		this.valueElement.textContent = selectedOptionElement.textContent.trim();

		this.optionElements.forEach((optionElement) => {
			const isActive =
				optionElement.getAttribute('data-tr-js-select-option-value') === this.nativeElement.value;

			optionElement.classList.toggle(this.stateClasses.isActive, isActive);
			optionElement.setAttribute('aria-selected', String(isActive));
		});
	}

	focusSelectedOption() {
		const selectedOptionElement =
			this.optionElements.find(
				(optionElement) =>
					optionElement.getAttribute('data-tr-js-select-option-value') === this.nativeElement.value
			) || this.optionElements[0];

		if (selectedOptionElement) selectedOptionElement.focus();
	}

	destroy() {
		if (this.buttonElement) {
			this.buttonElement.removeEventListener('click', this.onButtonClick);
			this.buttonElement.removeEventListener('keydown', this.onButtonKeydown);
		}

		this.optionElements.forEach((optionElement) => {
			optionElement.removeEventListener('click', this.onOptionClick);
			optionElement.removeEventListener('keydown', this.onOptionKeydown);
		});

		document.removeEventListener('click', this.onDocumentClick);
		document.removeEventListener('keydown', this.onDocumentKeydown);
	}
}

if (!customElements.get('tr-select')) {
	customElements.define('tr-select', TrSelect);
}
